import {
  hasValue,
  isEmpty,
  localStorage as localStorageHelper,
} from 'swipeclock-frontend';
import { getExpiresAt } from './offlinePunchRetrySchedule';
import {
  pickAllowedQueueFields,
  queueItemNeedsSanitizedRewrite,
  stripEphemeralQueueFields,
} from './offlineStorageAllowList';
import {
  readOfflineWebClockItem,
  writeOfflineWebClockItem,
} from './offlineWebClockStorage';
import { getOfflinePunchQueueStorageKey } from './webClockUserStorageKey';
import { purgeExpiredItems } from './offlinePunchQueueUtils';

const VALID_STATUSES = new Set([
  'pending',
  'syncing',
  'retrying',
  'synced',
  'permanent_failure',
]);

const VALID_SOURCES = new Set(['dashboard', 'prompts']);

const REQUIRED_STRING_FIELDS = [
  'id',
  'queuedAt',
  'punchDateTime',
  'status',
  'punch',
  'source',
  'requestType',
];

const sanitizeQueueItemForPersist = (item) => {
  const withoutEphemeral = stripEphemeralQueueFields(item);

  if (!withoutEphemeral || typeof withoutEphemeral !== 'object') {
    return {};
  }

  const allowed = pickAllowedQueueFields(withoutEphemeral);

  return {
    ...allowed,
    id: withoutEphemeral.id,
    queuedAt: withoutEphemeral.queuedAt,
    punchDateTime: withoutEphemeral.punchDateTime,
    expiresAt: withoutEphemeral.expiresAt,
    status: withoutEphemeral.status,
    retryCount: withoutEphemeral.retryCount,
    mobile: withoutEphemeral.mobile,
    punch: withoutEphemeral.punch,
    source: withoutEphemeral.source,
    requestType: withoutEphemeral.requestType,
  };
};

const normalizeQueueItem = (item) => {
  if (!item?.queuedAt) {
    return item;
  }

  if (hasValue(item.expiresAt)) {
    return item;
  }

  const expiresAt = getExpiresAt(item.queuedAt);

  if (!expiresAt) {
    return null;
  }

  return {
    ...item,
    expiresAt,
  };
};

const isValidQueueItem = (item) => {
  if (!item || typeof item !== 'object') {
    return false;
  }

  if (!REQUIRED_STRING_FIELDS.every((field) => hasValue(item[field]))) {
    return false;
  }

  if (!VALID_STATUSES.has(item.status)) {
    return false;
  }

  if (!VALID_SOURCES.has(item.source)) {
    return false;
  }

  if (typeof item.retryCount !== 'number') {
    return false;
  }

  if (typeof item.mobile !== 'boolean') {
    return false;
  }

  if (item.source === 'prompts' && isEmpty(item.clockPrompts)) {
    return false;
  }

  return true;
};

const parseQueue = (raw) => {
  if (!hasValue(raw)) {
    return [];
  }

  if (Array.isArray(raw)) {
    return raw;
  }

  if (typeof raw === 'string') {
    return JSON.parse(raw);
  }

  return [];
};

const isStorageAvailable = () =>
  localStorageHelper.isLocalStorageAvailable() || typeof window !== 'undefined';

const readQueueFromKey = (storageKey, { rewriteExpired = true } = {}) => {
  if (!storageKey) {
    return [];
  }

  const raw = readOfflineWebClockItem(storageKey);
  const parsed = parseQueue(raw);

  if (!Array.isArray(parsed)) {
    console.warn(
      '[offlinePunchStorage] Invalid queue format; resetting to [].'
    );
    return [];
  }

  const validItems = parsed
    .filter(isValidQueueItem)
    .map(normalizeQueueItem)
    .filter(Boolean);

  if (validItems.length !== parsed.length) {
    console.warn('[offlinePunchStorage] Dropped invalid queue entries.');
  }

  const now = Date.now();
  const purgedItems = purgeExpiredItems(validItems, now);
  const legacyFieldsRemoved = purgedItems.some(queueItemNeedsSanitizedRewrite);
  const sanitizedItems = purgedItems.map(sanitizeQueueItemForPersist);
  const expiredItemsRemoved = purgedItems.length < validItems.length;

  if (rewriteExpired && (expiredItemsRemoved || legacyFieldsRemoved)) {
    writeOfflineWebClockItem(storageKey, JSON.stringify(sanitizedItems));
  }

  return sanitizedItems;
};

export const readOfflinePunchQueue = (userKey) => {
  if (!hasValue(userKey) || !isStorageAvailable()) {
    return [];
  }

  const storageKey = getOfflinePunchQueueStorageKey(userKey);

  try {
    return readQueueFromKey(storageKey, { rewriteExpired: true });
  } catch (error) {
    console.warn(
      '[offlinePunchStorage] Corrupt queue data; resetting to [].',
      error
    );
    return [];
  }
};

export const writeOfflinePunchQueue = (queue, userKey) => {
  const storageKey = getOfflinePunchQueueStorageKey(userKey);

  if (!storageKey || !isStorageAvailable()) {
    return false;
  }

  const inputQueue = Array.isArray(queue) ? queue : [];
  const validItems = inputQueue
    .map(sanitizeQueueItemForPersist)
    .filter(isValidQueueItem)
    .map(normalizeQueueItem)
    .filter(Boolean);

  if (validItems.length !== inputQueue.length) {
    return false;
  }

  try {
    return writeOfflineWebClockItem(storageKey, JSON.stringify(validItems));
  } catch (error) {
    console.warn('[offlinePunchStorage] Failed to write queue.', error);
    return false;
  }
};

export const validateOfflinePunchQueueItem = isValidQueueItem;
